import { Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/features/auth';
import { lastNDays } from '@/shared/components/date-range-picker';
import { ErrorState } from '@/shared/components/error-state';
import { ExportActions } from '@/shared/components/export-actions';
import { formatNumber } from '@/shared/lib/format';

import { exportReport } from '../api/reports.api';
import { useOperationalReport } from '../api/reports.queries';
import type { ReportParams } from '../types/report.types';

const DAYS = 30;

const ordersLink = (providerId: number, failureCode?: string | null) => {
  const query = new URLSearchParams({ vendorId: String(providerId) });
  if (failureCode) query.set('failureCode', failureCode);
  return `/orders?${query.toString()}`;
};

/**
 * One provider's last 30 days — orders, success rate and why orders failed —
 * from the operational report filtered to that provider, across every country
 * in scope. Each failure reason opens those orders (orders.read).
 */
export function ProviderPerformance({ providerId }: { providerId: number }) {
  const { can } = usePermissions();
  const [params] = useState<ReportParams>(() => ({
    ...lastNDays(DAYS),
    groupBy: 'day',
    providerId,
  }));
  const report = useOperationalReport(params, can('reports.operations.read'));
  const canOrders = can('orders.read');

  if (!can('reports.operations.read')) {
    return canOrders ? <OrdersLookup providerId={providerId} /> : null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">Performance, last {DAYS} days</CardTitle>
        <span className="flex items-center gap-2">
          {canOrders && <OrdersLookup providerId={providerId} />}
          <ExportActions
            excel={() => exportReport('operational', params)}
            pdf={{
              list: 'report-provider-performance',
              rows: report.data?.rows.length ?? 0,
              filters: { from: params.from, to: params.to, provider: String(providerId) },
            }}
          />
        </span>
      </CardHeader>
      <CardContent>
        {report.isLoading ? (
          <Skeleton className="h-24" />
        ) : report.error || !report.data ? (
          <ErrorState error={report.error} onRetry={() => void report.refetch()} />
        ) : (
          <div className="grid gap-4 text-sm sm:grid-cols-2">
            <dl className="grid grid-cols-2 gap-3">
              <Metric label="Orders" value={formatNumber(report.data.totals.orders)} />
              <Metric
                label="Success rate"
                value={
                  report.data.totals.successRatePct === null
                    ? '—'
                    : `${report.data.totals.successRatePct}%`
                }
              />
              <Metric label="Completed" value={formatNumber(report.data.totals.completed)} />
              <Metric label="Failed" value={formatNumber(report.data.totals.failed)} />
            </dl>
            <div>
              <p className="text-xs font-medium">Why orders failed</p>
              {report.data.failureReasons.length === 0 ? (
                <p className="text-muted-foreground mt-1 text-xs">No failed orders.</p>
              ) : (
                <ul aria-label="Failure reasons" className="mt-1 divide-y text-xs">
                  {report.data.failureReasons.map((reason) => (
                    <li key={reason.code ?? 'none'} className="flex justify-between gap-2 py-1">
                      {canOrders && reason.code ? (
                        <Link
                          to={ordersLink(providerId, reason.code)}
                          className="text-primary font-mono hover:underline"
                        >
                          {reason.code}
                        </Link>
                      ) : (
                        <span className="font-mono">{reason.code ?? 'No code recorded'}</span>
                      )}
                      <span className="tabular-nums">{formatNumber(reason.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OrdersLookup({ providerId }: { providerId: number }) {
  return (
    <Button asChild size="sm" variant="outline">
      <Link to={ordersLink(providerId)}>
        <Search className="size-3.5" aria-hidden />
        Find this provider&apos;s orders
      </Link>
    </Button>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border rounded-md border p-2">
      <dt className="text-muted-foreground text-[11px] uppercase">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
